import React, { useState, useEffect } from 'react';
import { Card, Alert, Spinner, Button, Modal, Badge } from 'react-bootstrap';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NovoAgendamentoMaster from './NovoAgendamentoMaster';
import CardViewTable from '../../../components/CardViewTable/CardViewTable';
import { API_BASE_URL as API_BASE } from "../../../config";

function ConsultarAgendamentosMaster() {
  const navigate = useNavigate();
  const [agendamentos, setAgendamentos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clienteMasterId, setClienteMasterId] = useState(null);

  const [showModal, setShowModal] = useState(false);

  // CONFIRMAÇÃO DE EXCLUSÃO
  const [agendamentoParaExcluir, setAgendamentoParaExcluir] = useState(null);

  // FILTROS
  const [filtroFuncionario, setFiltroFuncionario] = useState("");
  const [filtroData, setFiltroData] = useState("");


  useEffect(() => {
    const cookieUsuario = Cookies.get("usuario");
    if (!cookieUsuario) {
      setError("Usuário não encontrado.");
      setLoading(false);
      return;
    }

    const usuario = JSON.parse(cookieUsuario);

    if (usuario?.role === "ClienteMaster") {
      setClienteMasterId(usuario.id);
      fetchData(usuario.id);
    } else {
      setError("Acesso negado. Você não é um cliente master.");
      setLoading(false);
    }
  }, []);

  const fetchData = async (masterId) => {
    setLoading(true);
    setError(null);

    try {
      const [agendamentosRes, funcionariosRes] = await Promise.all([
        axios.get(`${API_BASE}/Agendamentos?idClienteMaster=${masterId}`),
        axios.get(`${API_BASE}/Funcionarios?idClienteMaster=${masterId}`),
      ]);

      const agsOrdenados = [...agendamentosRes.data].sort((a, b) => {
        const dateA = new Date(a.dataHora || a.data || a.DataHora || a.Data);
        const dateB = new Date(b.dataHora || b.data || b.DataHora || b.Data);
        return dateA - dateB;
      });

      setAgendamentos(agsOrdenados);
      setFuncionarios(funcionariosRes.data);

    } catch (err) {
      console.error("Erro ao buscar dados:", err);
      setError("Erro ao carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  const handleMarcarRealizado = async (id) => {
    try {
      await axios.put(`${API_BASE}/Agendamentos/realizado/${id}`);
      fetchData(clienteMasterId);
    } catch (err) {
      console.error("Erro ao marcar realizado:", err);
      window.globalAlert("Erro ao marcar como realizado.", "error");
    }
  };

  const confirmarExclusao = async () => {
    if (!agendamentoParaExcluir) return;

    try {
      await axios.delete(`${API_BASE}/Agendamentos/${agendamentoParaExcluir}`);
      setAgendamentoParaExcluir(null);
      fetchData(clienteMasterId);
    } catch (err) {
      console.error("Erro ao excluir agendamento:", err);
      window.globalAlert("Erro ao excluir.", "error");
    }
  };

  // ===========================================
  // FILTRAGEM DOS AGENDAMENTOS
  // ===========================================
  const agendamentosFiltrados = agendamentos.filter((ag) => {

    // FILTRO POR FUNCIONÁRIO
    if (filtroFuncionario && ag.funcionarioId !== Number(filtroFuncionario)) {
      return false;
    }

    // FILTRO POR DATA
    if (filtroData) {
      const dataAg = new Date(
        ag.dataHora || ag.data || ag.DataHora || ag.Data
      )
        .toISOString()
        .split("T")[0];

      if (dataAg !== filtroData) return false;
    }

    return true;
  });

  if (loading) return <Spinner animation="border" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  const cardStyle = {
    backgroundColor: '#3c4049',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '25px',
    width: '100%',
    maxWidth: '100%',
    margin: '0',
  };

  return (
    <>
      <Card className="shadow-lg" style={cardStyle}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Card.Title className="fs-3">Consultar Agendamentos</Card.Title>
          <Button variant="success" onClick={() => setShowModal(true)}>
            + Novo Agendamento
          </Button>
        </div>

        {/* FILTROS */}
        <div className="d-flex gap-3 mb-4 flex-wrap">

          {/* FILTRO POR FUNCIONÁRIO */}
          <div>
            <label className="form-label text-light">Funcionário</label>
            <select
              className="form-select"
              value={filtroFuncionario}
              onChange={(e) => setFiltroFuncionario(e.target.value)}
            >
              <option value="">Todos</option>
              {funcionarios.map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>

          {/* FILTRO POR DATA */}
          <div>
            <label className="form-label text-light">Data</label>
            <input
              type="date"
              className="form-control"
              value={filtroData}
              onChange={(e) => setFiltroData(e.target.value)}
            />
          </div>

          {/* BOTÃO LIMPAR */}
          <div className="d-flex align-items-end">
            <Button
              variant="secondary"
              onClick={() => {
                setFiltroFuncionario("");
                setFiltroData("");
              }}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>

        {/* LISTA OU TABELA */}
        <Card.Body>
          {agendamentosFiltrados.length === 0 ? (
            <Alert variant="info">Nenhum agendamento encontrado.</Alert>
          ) : (
            <CardViewTable
              data={agendamentosFiltrados}
              keyField="id"
              cardTitleKey={(ag) => ag.servico?.nome || "—"}
              columns={[
                {
                  key: 'data',
                  header: 'Data',
                  render: (ag) => {
                    const data = ag.dataHora || ag.data || ag.DataHora || ag.Data;
                    return new Date(data).toLocaleString('pt-BR');
                  },
                },
                {
                  key: 'servico',
                  header: 'Serviço',
                  render: (ag) => ag.servico?.nome || "—",
                },
                {
                  key: 'funcionario',
                  header: 'Funcionário',
                  render: (ag) => ag.funcionario?.nome || "—",
                },
                {
                  key: 'cliente',
                  header: 'Cliente',
                  render: (ag) => ag.cliente?.nome || "—",
                },
                {
                  key: 'observacao',
                  header: 'Observação',
                  render: (ag) => ag.observacao || "—",
                },
                {
                  key: 'realizado',
                  header: 'Realizado',
                  render: (ag) => (
                    <Badge bg={ag.realizado ? "success" : "danger"}>
                      {ag.realizado ? "✅ Sim" : "❌ Não"}
                    </Badge>
                  ),
                },
                {
                  key: 'acoes',
                  header: 'Ações',
                  render: (ag) => (
                    <div className="d-flex flex-wrap gap-2">

                      {!ag.realizado && (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleMarcarRealizado(ag.id)}
                        >
                          Concluir
                        </Button>
                      )}

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setAgendamentoParaExcluir(ag.id)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          )}
        </Card.Body>
      </Card>

      {/* MODAL CONFIRMAR EXCLUSÃO */}
      <Modal show={!!agendamentoParaExcluir} onHide={() => setAgendamentoParaExcluir(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Exclusão</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Tem certeza que deseja excluir este agendamento?
          <br />
          Essa ação não pode ser desfeita.
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAgendamentoParaExcluir(null)}>
            Cancelar
          </Button>

          <Button variant="danger" onClick={confirmarExclusao}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL NOVO AGENDAMENTO */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Novo Agendamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <NovoAgendamentoMaster
            idClienteMaster={clienteMasterId}
            funcionarios={funcionarios}
            onAgendamentoCriado={() => {
              setShowModal(false);
              fetchData(clienteMasterId);
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default ConsultarAgendamentosMaster;
